import { createContext, useContext, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Wobbli } from 'wobbli/react';
import { useSettings, lookProps } from '../lib/settings';
import { useTheme, type ThemeChoice } from '../lib/theme';
import { BLOBATAR_URL, GITHUB_URL } from '../config';
import { BlobMark } from './ui';

// the header's "Install & share" button opens the studio's dialog
const ShareCtx = createContext<{ open: boolean; setOpen: (v: boolean) => void }>({ open: false, setOpen: () => {} });
export const useShareDialog = () => useContext(ShareCtx);
export function ShareProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return <ShareCtx.Provider value={{ open, setOpen }}>{children}</ShareCtx.Provider>;
}

const MOON = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 14.5A8.5 8.5 0 0 1 9.5 4A8.5 8.5 0 1 0 20 14.5Z" /></svg>;
const SUN = <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2.5V5M12 19v2.5M2.5 12H5M19 12h2.5M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8" /></svg>;
const SCREEN = <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="12.5" rx="2" /><path d="M8.5 20h7M12 16.5V20" /></svg>;
const THEMES: Record<ThemeChoice, [ReactNode, string, ThemeChoice]> = { system: [SCREEN, 'System', 'light'], light: [SUN, 'Light', 'dark'], dark: [MOON, 'Dark', 'system'] };
const GH = <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.4 5.4 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" /><path d="M9 18c-4.51 2-5-2-7-2" /></svg>;

export function Header() {
  const { s } = useSettings();
  const loc = useLocation();
  const studio = loc.pathname === '/studio';
  const [choice, dark, set] = useTheme();
  const m = THEMES[choice];
  const { setOpen } = useShareDialog();
  return (
    <header className="top">
      <div className="wrap top-in">
        <Link className="brand" to="/">
          <span className="brand-av"><Wobbli {...lookProps(s)} size="100%" state="idle" label="Wobbli" /></span>
          <span className="brand-tx">Wobbli</span>
        </Link>
        <nav className="nav" aria-label="Pages">
          <NavLink to="/" end>Home</NavLink>
          <NavLink to="/studio">Studio</NavLink>
          <NavLink to="/states"><span className="nav-long">All states</span><span className="nav-short">States</span></NavLink>
          <NavLink to="/recipes">Recipes</NavLink>
          <NavLink to="/about">About</NavLink>
        </nav>
        <a className="gh-link has-icon" href={GITHUB_URL} target="_blank" rel="noopener" title="GitHub" aria-label="GitHub">{GH}<span className="gh-text">GitHub</span></a>
        <button type="button" className="icon-btn theme-btn" onClick={() => set(m[2])}
          aria-label={'Theme: ' + m[1].toLowerCase() + (choice === 'system' ? ' (' + (dark ? 'dark' : 'light') + ')' : '') + '. Switch to ' + THEMES[m[2]][1].toLowerCase()}
          title={'Theme: ' + m[1] + (choice === 'system' ? ' (' + (dark ? 'dark' : 'light') + ')' : '')}>{m[0]}</button>
        {studio
          ? <button type="button" className="btn btn-ink btn-sm" onClick={() => setOpen(true)}><span className="share-long">Install &amp; share</span><span className="share-short">Share</span></button>
          : <Link className="btn btn-ink btn-sm top-cta" to="/studio">Open the studio</Link>}
      </div>
    </header>
  );
}

export function TabBar() {
  return (
    <nav className="tabbar" aria-label="Pages">
      <NavLink to="/" end><span className="ti"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 10.5 12 3.5l8.5 7" /><path d="M5.5 9v11.5h13V9" /><path d="M10 20.5v-5.5h4v5.5" /></svg></span>Home</NavLink>
      <NavLink to="/studio"><span className="ti"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6.5h9M17 6.5h3M4 12h3M11 12h9M4 17.5h11M19 17.5h1" /><circle cx="15" cy="6.5" r="2" /><circle cx="9" cy="12" r="2" /><circle cx="17" cy="17.5" r="2" /></svg></span>Studio</NavLink>
      <NavLink to="/states"><span className="ti"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="7" height="7" rx="2" /><rect x="13" y="4" width="7" height="7" rx="2" /><rect x="4" y="13" width="7" height="7" rx="2" /><rect x="13" y="13" width="7" height="7" rx="2" /></svg></span>All states</NavLink>
      <NavLink to="/about"><span className="ti"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8.5" r="4" /><path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" /></svg></span>About</NavLink>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="wrap foot">
      <div className="foot-brand">
        <Link className="brand" to="/"><BlobMark />Wobbli</Link>
        <p>A side project by Hendrik. Silhouettes and faces come from <a href={BLOBATAR_URL} target="_blank" rel="noopener">blobatar</a> 2.7.0 (MIT licence, © 2026 Alain). States, poses and holiday costumes are layers drawn on top of it, and none of them change the body colour.</p>
      </div>
      <nav className="foot-col" aria-label="Pages (footer)"><b>Pages</b><Link to="/">Home</Link><Link to="/studio">Studio</Link><Link to="/states">All states</Link><Link to="/recipes">Recipes</Link><Link to="/about">About</Link></nav>
      <nav className="foot-col" aria-label="Elsewhere"><b>Elsewhere</b><a href="https://kleine.ai" target="_blank" rel="noopener">kleine.ai</a><a href={GITHUB_URL} target="_blank" rel="noopener">GitHub</a><a href={BLOBATAR_URL} target="_blank" rel="noopener">blobatar</a></nav>
    </footer>
  );
}
